<template>
  <div v-if="groups.length">
    <h2>{{ t('title') }}</h2>
    <ul class="mt-3 space-y-2">
      <template v-for="group in groups" :key="group.allocationId">
        <li
          v-for="inspection in group.inspections"
          :key="inspection.id"
          class="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-obsidian-100 p-2"
        >
          <button
            v-if="getPreviewUrls(inspection).length"
            type="button"
            class="flex shrink-0 cursor-pointer appearance-none -space-x-3 border-0 bg-transparent p-0"
            :aria-label="t('show')"
            @click="handleOpen(inspection, group.fuelType)"
          >
            <img
              v-for="url in getPreviewUrls(inspection)"
              :key="url"
              :src="url"
              alt=""
              class="aspect-video h-10 rounded object-cover ring-2 ring-white"
              loading="lazy"
            />
          </button>
          <div class="min-w-0 flex-1">
            <p class="flex flex-wrap items-center gap-2 font-semibold">
              {{ getPhaseLabel(inspection) }}
              <LicencePlate v-if="groups.length > 1 && group.licencePlate">
                {{ group.licencePlate }}
              </LicencePlate>
            </p>
            <p class="text-sm text-gray-500">{{ getSummary(inspection) }}</p>
          </div>
          <CVButton variant="secondary" size="sm" @click="handleOpen(inspection, group.fuelType)">
            <ImagesIcon class="size-4" />
            {{ t('show') }}
          </CVButton>
        </li>
      </template>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { sortBy } from 'lodash';
import { InspectionPhase, type InspectionDto } from '@carvia/ros-client-types';
import { ImagesIcon } from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';
import LicencePlate from '@/components/LicencePlate.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { FuelType } from '@/entities/cars/fuel-type.enum';
import { formatDate } from '@/hooks/use-formatted-date';
import { useBookingInspections } from '@/queries/use-inspections';
import { getUserName } from '@/utils/get-user-name';
import { getInspectionShotViews, getShotImageUrl, countPendingImages } from './inspection-state';
import { openInspectionLightbox, type InspectionLightboxSlide } from './open-inspection-lightbox';
import { useShotLabel } from './use-shot-label';

const PREVIEW_COUNT = 4;

const props = defineProps<{ booking: Booking }>();

const { t } = useI18n();
const { getShotLabel } = useShotLabel();
const { data: inspections } = useBookingInspections(computed(() => props.booking.id));

const groups = computed(() =>
  props.booking.allocations
    .map((allocation) => ({
      allocationId: allocation.id,
      licencePlate: allocation.car.licencePlate,
      fuelType: allocation.car.fuelType ?? allocation.car.vehicleType.fuelType,
      inspections: sortBy(
        (inspections.value ?? []).filter((inspection) => inspection.allocationId === allocation.id),
        (inspection) => (inspection.phase === InspectionPhase.HANDOVER ? 0 : 1),
      ),
    }))
    .filter((group) => group.inspections.length),
);

const getPhaseLabel = (inspection: InspectionDto) =>
  t(inspection.phase === InspectionPhase.HANDOVER ? 'handover' : 'return');

const getPreviewUrls = (inspection: InspectionDto) =>
  getInspectionShotViews(inspection)
    .map((view) => getShotImageUrl(view))
    .filter((url): url is string => !!url)
    .slice(0, PREVIEW_COUNT);

const getSummary = (inspection: InspectionDto) => {
  const views = getInspectionShotViews(inspection);
  const captured = views.filter((view) => view.state === 'captured').length;
  const skipped = views.filter((view) => view.state === 'skipped').length;
  const parts = [t('captured', { count: captured, total: inspection.expectedShotKeys.length })];
  if (skipped) parts.push(t('skipped', { count: skipped }));
  const pending = countPendingImages(inspection);
  if (pending) parts.push(t('pending', { count: pending }));
  return parts.join(', ');
};

const handleOpen = (inspection: InspectionDto, fuelType?: FuelType | null) => {
  const views = getInspectionShotViews(inspection);
  const total = String(views.length).padStart(2, '0');
  const slides = views.map((view, index): InspectionLightboxSlide => {
    const { shot } = view;
    const label = getShotLabel(view.key, fuelType);
    const meta = shot
      ? [
          formatDate(shot.recordedDate, 'dd.MM.yyyy HH:mm'),
          shot.recordedBy && getUserName(shot.recordedBy),
        ]
          .filter(Boolean)
          .join(', ')
      : undefined;
    const slide = {
      title: `${String(index + 1).padStart(2, '0')}/${total} ${label}`,
      meta: [getPhaseLabel(inspection), meta].filter(Boolean).join(', '),
      imageUrl: getShotImageUrl(view),
    };
    if (slide.imageUrl) return slide;
    if (view.state === 'skipped') {
      return { ...slide, notice: { title: t('skippedShot'), text: shot?.skipReason ?? '' } };
    }
    return { ...slide, notice: { title: t(shot?.isImagePending ? 'uploading' : 'notRecorded') } };
  });
  openInspectionLightbox(slides);
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Vehicle photos",
    "show": "Show photos",
    "handover": "Handover",
    "return": "Return",
    "captured": "{count} of {total} photos",
    "skipped": "{count} skipped",
    "pending": "{count} still uploading",
    "skippedShot": "Skipped",
    "uploading": "Photo is still uploading from the capturing device.",
    "notRecorded": "Not recorded"
  },
  "de": {
    "title": "Fahrzeugfotos",
    "show": "Fotos ansehen",
    "handover": "Übergabe",
    "return": "Rückgabe",
    "captured": "{count} von {total} Fotos",
    "skipped": "{count} übersprungen",
    "pending": "{count} werden noch hochgeladen",
    "skippedShot": "Übersprungen",
    "uploading": "Foto wird noch vom aufnehmenden Gerät hochgeladen.",
    "notRecorded": "Nicht erfasst"
  },
  "es": {
    "title": "Fotos del vehículo",
    "show": "Ver fotos",
    "handover": "Entrega",
    "return": "Devolución",
    "captured": "{count} de {total} fotos",
    "skipped": "{count} omitidas",
    "pending": "{count} subiéndose todavía",
    "skippedShot": "Omitida",
    "uploading": "La foto se sigue subiendo desde el dispositivo que la hizo.",
    "notRecorded": "No registrada"
  }
}
</i18n>
